// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Accordion, Box } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { OutlinedIconButton } from 'components/DataEntry';
import { DownloadIcon } from 'components/Icons';
import { usePageContentContext } from 'components/Layout';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { InstanceSummaryByID } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/ContainerVulnerability/InstanceSummaryByID';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { saveAsFile } from 'utils/download';

interface Props {
  vulnerability?: boolean;
}

export const ContainerVulnerability: FC<Props> = props => {
  const { vulnerability } = props;
  const {
    containerVulnerability,
    resourceInsight,
    resourceType,
    resourceDetails,
    resourceId,
    downloadReport,
    resourceUuid,
    resourceAccountId,
  } = useSelector(selectActiveResource);
  const { environmentId } = useAccessBoundary();
  const { contentHeight } = usePageContentContext();

  const dispatch = useDispatch();

  useEffect(() => {
    let instance_ids: string[] = [resourceId];
    if (
      resourceType === NativeResources.ElasticKubernetesService ||
      resourceType === NativeResources.Aks
    ) {
      instance_ids = map(
        resourceInsight.data?.instances,
        o => o.split(',')?.[0]?.split('instance-id: ')[1],
      );
    } else if (resourceType === NativeResources.Gcpgke) {
      instance_ids = resourceDetails.data?.connection?.GCPComputeEngine;
    } else if (
      resourceType === NativeResources.ElasticContainerService ||
      resourceType === NativeResources.EcsCluster
    ) {
      instance_ids = map(
        resourceDetails.data?.config?.containerInstances,
        o => o?.ec2InstanceId,
      );
    }
    dispatch(actions.resetContainerVulnerability());

    if (!!instance_ids?.length) {
      dispatch(
        actions.getContainerVulnerability({
          q: {
            resourceUuid,
            getContainersRequest: { instance_ids, vulnerable: vulnerability },
          },
        }),
      );
    }
  }, [
    resourceType,
    resourceInsight.data,
    dispatch,
    resourceDetails.data,
    vulnerability,
    resourceId,
    resourceUuid,
  ]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetContainerVulnerability());
    };
  }, [dispatch]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Container ID</Box>,
      accessor: 'container_id',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={NativeResources.Container}
          tooltip={value}
        >
          {value}
        </WithResourceIcon>
      ),
    },
    {
      header: 'Container Image',
      align: 'left',
      accessor: 'image',
    },
    {
      header: 'POD Name',
      align: 'left',
      accessor: 'pod',
    },
    { header: 'Namespace', align: 'left', accessor: 'namespace' },
  ];
  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <MetaAccordionItem
        height={contentHeight - 250 + 'px'}
        iconType={NativeResources.Container}
        label={
          vulnerability
            ? 'Vulnerabilities detected in running containers'
            : 'Running containers'
        }
        onClickDownload={() => {}}
        totalCount={containerVulnerability.data?.length ?? 0}
        rightHeader={() =>
          vulnerability && (
            <OutlinedIconButton
              leftIcon={<DownloadIcon />}
              isLoading={downloadReport.isLoading}
              tooltip="Download all open vulnerabilities"
              onClick={() => {
                dispatch(
                  actions.downloadReport({
                    q: {
                      accountId: [resourceAccountId],
                      environmentId,
                      resourceId: [resourceUuid],
                      resourceType,
                    },
                    onSuccess: res => {
                      saveAsFile(
                        res,
                        'Ariksa - Vulnerabilities_' +
                          resourceId +
                          '_' +
                          dayjs().format('DD-MMM-YYYY h.mm A') +
                          '.csv',
                        'text/csv;charset=utf-8;',
                      );
                    },
                  }),
                );
              }}
            >
              Download CSV
            </OutlinedIconButton>
          )
        }
        panel={
          <Table
            columns={columns}
            data={containerVulnerability.data ?? []}
            /*onRowClick={row =>
              dispatch(
                actions.getInstanceSummary({
                  q: {
                    instanceId: row?.instance_id,
                    containerId: row.container_id,
                  },
                }),
              )
            }*/
            cursor="pointer"
            isLoading={containerVulnerability.isLoading}
            subComponent={({ row, expanded }) => (
              <InstanceSummaryByID row={row} isExpanded={expanded} />
            )}
          />
        }
        iconBGColor="primary"
        isLoading={
          resourceInsight.isLoading || containerVulnerability.isLoading
        }
      />
    </Accordion>
  );
};
